import { supabase } from './supabaseClient'
import { createLogger } from './logger'
import { invokeEdgeFunction } from './edgeFunctionClient'

const log = createLogger('RoleService')

export interface UserRole {
  id: string
  name: string
  description: string
  permissions: string[]
}

export interface UserWithRole {
  id: string
  email: string
  name?: string
  roles: UserRole[]
  created_at: string
  last_sign_in_at?: string
}

export interface InviteUserRequest {
  email: string
  firstName?: string
  lastName?: string
  role: string
  sendInvite?: boolean
}

/**
 * Role-based permission service that integrates Auth0 with Supabase
 */
export class RoleService {
  /**
   * Get all available roles from Supabase
   */
  async getRoles(): Promise<UserRole[]> {
    const { data, error } = await supabase
      .from('roles')
      .select(
        `
        id,
        name,
        description,
        role_permissions (
          permission_id,
          permissions (
            name
          )
        )
      `,
      )
      .order('name')

    if (error) throw error

    return data.map((role) => ({
      id: role.id,
      name: role.name,
      description: role.description,
      permissions:
        role.role_permissions?.map((rp: any) => rp.permissions?.name).filter(Boolean) || [],
    }))
  }

  /**
   * Get all users with their roles
   */
  async getUsersWithRoles(): Promise<UserWithRole[]> {
    let token = (window as any).__auth0IdToken as string | undefined
    if (!token) {
      for (let i = 0; i < 15 && !token; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100))
        token = (window as any).__auth0IdToken || (window as any).__auth0Token
      }
    }

    const headers: Record<string, string> = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    const { data: fnData, error: fnError } = await invokeEdgeFunction('auth0-management-users', {
      page: 0, per_page: 100, token,
    })

    if (fnError) {
      const { data: fallbackData, error: fallbackError } = await supabase.rpc('get_all_user_roles')
      if (fallbackError) {
        throw fallbackError
      }

      const result = (fallbackData as any[]) || []
      return result.map((user) => ({
        id: user.user_id,
        email: user.email,
        name: user.name,
        roles: (user.roles || []).map((roleName: string) => ({
          id: roleName,
          name: roleName,
          description: '',
          permissions: [],
        })),
        created_at: user.created_at,
        last_sign_in_at: user.last_sign_in_at,
      }))
    }

    const directoryUsers: any[] = Array.isArray((fnData as any)?.users)
      ? (fnData as any).users
      : Array.isArray(fnData)
        ? (fnData as any)
        : []

    // Then get all user roles
    const { data: userRolesData, error: userRolesError } = await supabase
      .from('user_roles')
      .select(`
        user_id,
        roles (
          id,
          name,
          description
        )
      `)

    if (userRolesError) throw userRolesError

    // Build email → user_id mapping from admin_users for cross-referencing
    const { data: adminRows } = await supabase
      .from('admin_users')
      .select('user_id, email')

    const emailToUserId = new Map<string, string>()
    ;(adminRows || []).forEach((row: any) => {
      if (row.email && row.user_id) {
        emailToUserId.set(row.email.toLowerCase(), row.user_id)
      }
    })

    // Group roles by user ID
    const rolesMap = new Map<string, UserRole[]>()
    userRolesData.forEach((ur: any) => {
      if (!rolesMap.has(ur.user_id)) {
        rolesMap.set(ur.user_id, [])
      }
      if (ur.roles) {
        rolesMap.get(ur.user_id)!.push({
          id: ur.roles.id,
          name: ur.roles.name,
          description: ur.roles.description,
          permissions: [],
        })
      }
    })

    // Combine users with their roles (match by direct ID or by email → UUID)
    return directoryUsers.map((user: any) => {
      const email = (user.email || '').toLowerCase()
      let roles = rolesMap.get(user.id)
      if (!roles && email) {
        const resolvedId = emailToUserId.get(email)
        if (resolvedId) {
          roles = rolesMap.get(resolvedId)
        }
      }
      return {
        id: user.id,
        email: user.email,
        name: user.user_metadata?.name || user.user_metadata?.full_name || user.name,
        roles: roles || [],
        created_at: user.created_at,
        last_sign_in_at: user.last_login || user.last_sign_in_at,
      }
    })
  }

  /**
   * Assign role to user
   */
  async assignRoleToUser(userId: string, roleId: string): Promise<void> {
    const { error } = await supabase.from('user_roles').insert({
      user_id: userId,
      role_id: roleId,
    })

    if (error) throw error
  }

  /**
   * Remove role from user
   */
  async removeRoleFromUser(userId: string, roleId: string): Promise<void> {
    const { error } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role_id', roleId)

    if (error) throw error
  }

  /**
   * Check if user has specific role
   */
  async userHasRole(userId: string, roleName: string): Promise<boolean> {
    // Fallback to client-side check using getUserRoles to avoid cross-table filter typing issues
    const roles = await this.getUserRoles(userId)
    return roles.some((r) => r.name === roleName)
  }

  /**
   * Get user's roles
   */
  async getUserRoles(userId: string): Promise<UserRole[]> {
    log.debug('Getting roles for user ID:', userId)
    const { data, error } = await supabase
      .from('user_roles')
      .select(
        `
        roles (
          id,
          name,
          description
        )
      `,
      )
      .eq('user_id', userId)

    log.debug('Supabase query result:', { data, error })

    if (error) throw error

    const result = (data as any[])
      .map((item: any) => {
        const role = item.roles
        if (!role || typeof role !== 'object') return null
        return {
          id: role.id,
          name: typeof role.name === 'string' ? role.name : '',
          description: typeof role.description === 'string' ? role.description : '',
          permissions: [] as string[],
        } as UserRole
      })
      .filter((role): role is UserRole => Boolean(role?.id && role?.name))

    log.debug('Mapped roles result:', result)
    return result
  }

  async getUserRolesForEmail(email: string): Promise<UserRole[]> {
    if (!email) return []

    try {
      // Use the get_user_id_by_email RPC to resolve email → Supabase user UUID.
      // This avoids direct auth.users access which is blocked from the JS client.
      const { data: rpcData, error: rpcErr } = await supabase.rpc(
        'get_user_id_by_email',
        { email_param: email.toLowerCase() },
      )

      let userId = rpcErr ? null : rpcData

      // Fallback: check admin_users table which stores email directly
      if (!userId) {
        const { data: adminRow } = await supabase
          .from('admin_users')
          .select('user_id')
          .ilike('email', email)
          .limit(1)
          .maybeSingle()

        userId = adminRow?.user_id
      }

      if (!userId) {
        log.debug('No user_id found for email:', email)
        return []
      }

      return this.getUserRoles(userId)
    } catch (err) {
      log.error('Failed to lookup user roles by email:', err)
      return []
    }
  }

  /**
   * Check if user has permission
   */
  async userHasPermission(userId: string, permissionName: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('user_roles')
      .select(
        `
        roles (
          role_permissions (
            permissions (
              name
            )
          )
        )
      `,
      )
      .eq('user_id', userId)

    if (error) throw error

    const permissions = data.flatMap(
      (ur: any) =>
        ur.roles?.role_permissions?.map((rp: any) => rp.permissions?.name).filter(Boolean) || [],
    )

    return permissions.includes(permissionName)
  }

  /**
   * Create user invitation (placeholder for Auth0 integration)
   */
  async inviteUser(request: InviteUserRequest): Promise<{ success: boolean; message: string }> {
    try {
      // For now, we'll use the existing manual invitation system.
      // We avoid querying auth.users directly to keep type-safety with the generated client.

      // For now, return manual instructions
      return {
        success: true,
        message: `To invite ${request.email} with ${request.role} role:

1. Go to Supabase Dashboard: Auth > Users
2. Click "Invite a user" 
3. Enter email: ${request.email}
4. Set redirect URL: ${typeof window !== 'undefined' ? window.location.origin : 'https://your-app-domain'}/onboard
5. Click "Send invitation"

After the user signs up, assign the ${request.role} role in the Users page.`,
      }
    } catch (error) {
      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  }
}

export const roleService = new RoleService()
